import { useMemo } from "react";
import { useSelector } from "react-redux";
import { fromJS } from "immutable";

/**
 * Monthly AI token budget for Content Studio (tenant-level).
 * Content Studio itself is not plan-gated — only LLM calls consume tokens and may return 429.
 */
export function useAiContentTokenQuota() {
  const tokenUsage =
    useSelector((state: any) => state.billingReducer.get("tokenUsage")) || fromJS({});
  const userLimit =
    useSelector((state: any) => state.billingReducer.get("userLimit")) || fromJS({});

  return useMemo(() => {
    const totalTokens = Number(tokenUsage.get("totalTokens") || 0);
    const limit = Number(userLimit.getIn(["entitlements", "aiContentTokensPerMonth"]) || 0);
    const hasAllowance = limit > 0;
    const remaining = hasAllowance ? Math.max(0, limit - totalTokens) : 0;
    const isExhausted = hasAllowance && totalTokens >= limit;
    /** True when another AI request would be rejected (server returns 429). */
    const isAiQuotaExhausted = !hasAllowance || isExhausted;
    const canRunAi = hasAllowance && !isExhausted;
    const percentUsed =
      limit > 0 ? Math.min(100, Math.round((totalTokens / limit) * 100)) : 0;

    return {
      totalTokens,
      limit,
      remaining,
      hasAllowance,
      isExhausted,
      isAiQuotaExhausted,
      canRunAi,
      percentUsed,
    };
  }, [tokenUsage, userLimit]);
}
